import React, { useRef } from 'react';
import styled from 'styled-components';
import { UseFormRegister } from 'react-hook-form';
import { RawFormFillerData } from 'shared-utils/types/formFiller';
import { FieldState } from '../../../lib/formFiller';
import { InnerCheckbox } from './CheckboxFieldInput';

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  cursor: pointer;
`;

const RadioWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const HiddenRadio = styled.input`
  position: absolute;
  opacity: 0;
  // Allow hover to pass through to the styled checkbox
  pointer-events: none;
  width: 100%;
  height: 100%;
  margin: 0;
  z-index: 1;
  &:focus {
    outline: none;
  }
`;

/**
 * Single radio button field component for PDF forms
 * Each radio button is positioned individually with shared group names
 */
export function RadioFieldInput({
  fieldState,
  groupName,
  value,
  optionValue,
  register,
}: {
  fieldState: FieldState;
  groupName: string;
  optionValue: string;
  value: string;
  register: UseFormRegister<RawFormFillerData>;
}) {
  // Get the register props and wrap the onClick to handle deselection
  const registerProps = register(groupName);
  const hiddenRef = useRef<HTMLInputElement | null>(null);

  const isChecked = value === optionValue;
  const handleClick = (_event: React.MouseEvent<HTMLInputElement>) => {
    if (fieldState.isReadOnly) return;
    // If clicking on an already selected radio button, clear the value
    if (isChecked) {
      // Create a synthetic change event with empty value to clear the selection
      const syntheticEvent = {
        target: {
          name: registerProps.name,
          value: '',
        },
      } as React.ChangeEvent<HTMLInputElement>;
      registerProps.onChange(syntheticEvent);
    } else {
      hiddenRef.current?.click();
    }
  };
  const hiddenElement = (
    <HiddenRadio
      {...registerProps}
      ref={(element) => {
        registerProps.ref(element);
        hiddenRef.current = element;
      }}
      type="radio"
      value={optionValue}
      id={fieldState.id}
      disabled={fieldState.isReadOnly}
      tabIndex={-1}
    />
  );
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      hiddenRef.current?.click();
    }
  };

  return (
    <Container>
      <RadioWrapper>
        <InnerCheckbox
          fieldState={fieldState}
          hiddenElement={hiddenElement}
          isChecked={isChecked}
          onKeyDown={handleKeyDown}
          onClick={handleClick}
        />
      </RadioWrapper>
    </Container>
  );
}
